import React, { useState, useMemo } from "react";
import {
  TextField,
  Grid,
  MenuItem,
  Box,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import BusinessIcon from "@mui/icons-material/Business";
import LockResetIcon from "@mui/icons-material/LockReset";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from 'react-i18next';

const UserGrid = ({
  users,
  onEditOrganizations,
  onEditPassword,
  onEditUser,
  onDeleteUser,
  currentUserRole, // Rol del usuario actual
  currentUserId, // ID del usuario actual
}) => {
  const [filters, setFilters] = useState({
    name: "",
    email: "",
    organization: "",
    project: "",
  });
  const [pageSize, setPageSize] = useState(10);

  const { t } = useTranslation();

  const uniqueOrganizations = useMemo(() => {
    return Array.from(new Set(users.flatMap((u) => u.organizationNames || [])));
  }, [users]);

  const uniqueProjects = useMemo(() => {
    return Array.from(new Set(users.flatMap((u) => u.projectNames || [])));
  }, [users]);

  const handleChange = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Función para determinar si el usuario actual puede editar a otro usuario
  const canEditUser = (targetUser) => {
    // Si es el mismo usuario, siempre puede editarse a sí mismo
    if (targetUser.uid === currentUserId) {
      return true;
    }

    // Lógica según el rol del usuario actual
    switch (currentUserRole) {
      case "super-admin":
        // Super admin puede editar a cualquiera
        return true;
      case "admin-grafton":
        // Admin Grafton puede editar admin y user
        return ["admin", "user"].includes(targetUser.rol);
      case "admin":
        // Admin solo puede editar user
        return targetUser.rol === "user";
      default:
        // Otros roles no pueden editar a nadie más
        return false;
    }
  };

  const filteredUsers = users.filter((u) => {
    const nameMatch = u.name?.toLowerCase().includes(filters.name.toLowerCase());
    const emailMatch = u.email?.toLowerCase().includes(filters.email.toLowerCase());
    const orgMatch =
      filters.organization === "" || u.organizationNames?.includes(filters.organization);
    const projMatch =
      filters.project === "" || u.projectNames?.includes(filters.project);
    return nameMatch && emailMatch && orgMatch && projMatch;
  });

  const columns = [
    { field: "name", headerName: t('name'), flex: 1 },
    { field: "email", headerName: t('email'), flex: 1 },
    { field: "rol", headerName: t('role'), flex: 1 },
    {
      field: "actions",
      headerName: t('actions'),
      flex: 0.5,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const canEdit = canEditUser(params.row);

        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title={canEdit ? t('users.changePassword') : t('users.noEditAllowed')}>
              <span>
                <IconButton
                  size="small"
                  disabled={!canEdit}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditPassword?.(params.row);
                  }}
                >
                  <LockResetIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title={canEdit ? t('users.changeOrganizations') : t('users.noEditAllowed')}>
              <span>
                <IconButton
                  size="small"
                  disabled={!canEdit}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditOrganizations?.(params.row);
                  }}
                >
                  <BusinessIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title={canEdit ? t('users.generalEdit') : t('users.noEditAllowed')}>
              <span>
                <IconButton
                  size="small"
                  disabled={!canEdit}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditUser?.(params.row);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title={canEdit ? t('users.deleteUser') : t('users.noEditAllowed')}>
              <span>
                <IconButton
                  size="small"
                  disabled={!canEdit}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteUser?.(params.row);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label={t('name')}
              name="name"
              fullWidth
              value={filters.name}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label={t('email')}
              name="email"
              fullWidth
              value={filters.email}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              label={t('organization')}
              name="organization"
              fullWidth
              value={filters.organization}
              onChange={handleChange}
            >
              <MenuItem value="">Todas</MenuItem>
              {uniqueOrganizations.map((org, idx) => (
                <MenuItem key={idx} value={org}>
                  {org}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              label={t('project')}
              name="project"
              fullWidth
              value={filters.project}
              onChange={handleChange}
            >
              <MenuItem value="">Todos</MenuItem>
              {uniqueProjects.map((proj, idx) => (
                <MenuItem key={idx} value={proj}>
                  {proj}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ height: 500, width: "100%" }}>
        <DataGrid
          rows={filteredUsers}
          columns={columns}
          pageSize={pageSize}
          onPageSizeChange={(newSize) => setPageSize(newSize)}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </Box>
    </>
  );
};

export default UserGrid;